import { useState, useRef, useMemo, useEffect } from 'react'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import {
  Avatar, Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography,
} from '@mui/material'
import {
  Assignment as TaskIcon,
  AccessTime as ClockIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as RedirectIcon,
  CheckCircleOutline as CheckIcon,
  HourglassEmpty as PendingIcon,
  TrendingUp as InProgressIcon,
  AssignmentTurnedIn as TotalIcon,
  FilterList as FilterListIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  FilterAltOff as FilterAltOffIcon,
} from '@mui/icons-material'
import { PageHeader } from '../components/shared/PageHeader'
import { themeColors } from '../app/layout/theme'
import { tasksApi, Task, TaskStatus, TaskPriority, TasksSummary } from '../lib/api/tasksApi'
import { dealsApi } from '../lib/api/dealsApi'
import { leadTasksApi } from '../lib/api/leadTasksApi'
import { leadNotesApi } from '../lib/api/leadNotesApi'
import { leadsApi } from '../lib/api/leadsApi'
import { salesApi } from '../lib/api/salesApi'
import { retentionApi } from '../lib/api/retentionApi'
import { authStore } from '../lib/stores/authStore'
import { usePermission } from '../lib/hooks/usePermission'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { orgsApi, lobsApi } from '../lib/api/tenantsApi'
import { toast } from 'react-toastify'
import { format, isPast, isToday, isTomorrow } from 'date-fns'
import { FilterDef, UnifiedFilters, EMPTY_UNIFIED_FILTERS, applyUnifiedDateFilter, countActiveFilters, UnifiedFilterDialog } from '../components/shared/UnifiedFilterDialog'
import { SearchBar } from '../components/ui/SearchBar'
import { TaskModal } from '../components/shared/TaskModal'
import { DeleteDialog } from '../components/shared/DeleteDialog'

// ── Config ────────────────────────────────────────────────────────────────────

const BASE_TASK_FILTERS: FilterDef[] = [
  { type: 'date', label: 'Due Date' },
  {
    type: 'checkbox',
    key: 'priority',
    label: 'Priority',
    options: [
      { label: 'Urgent', value: 'URGENT' },
      { label: 'High', value: 'HIGH' },
      { label: 'Medium', value: 'MEDIUM' },
      { label: 'Low', value: 'LOW' },
    ]
  }
]

interface Column {
  id: TaskStatus
  label: string
  color: string
  bgColor: string
  dotColor: string
}

const COLUMNS: Column[] = [
  { id: 'PENDING', label: 'Pending', color: '#b45309', bgColor: '#fef3c7', dotColor: '#f59e0b' },
  { id: 'IN_PROGRESS', label: 'In Progress', color: '#1d4ed8', bgColor: '#dbeafe', dotColor: '#3b82f6' },
  { id: 'DONE', label: 'Done', color: '#15803d', bgColor: '#dcfce7', dotColor: '#22c55e' },
]

const PRIORITY_META: Record<TaskPriority, { label: string; color: string; bg: string }> = {
  LOW: { label: 'Low', color: '#6b7280', bg: '#f3f4f6' },
  MEDIUM: { label: 'Medium', color: '#d97706', bg: '#fef3c7' },
  HIGH: { label: 'High', color: '#ea580c', bg: '#fff7ed' },
  URGENT: { label: 'Urgent', color: '#dc2626', bg: '#fef2f2' },
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDueDate(dueDate: string | null): { text: string; color: string; warn: boolean } {
  if (!dueDate) return { text: 'No due date', color: '#94a3b8', warn: false }
  const d = new Date(dueDate)
  if (isPast(d) && !isToday(d)) return { text: `Overdue · ${format(d, 'dd MMM')}`, color: '#ef4444', warn: true }
  if (isToday(d)) return { text: 'Due today', color: '#f59e0b', warn: true }
  if (isTomorrow(d)) return { text: 'Due tomorrow', color: '#f59e0b', warn: false }
  return { text: format(d, 'dd MMM yyyy'), color: '#64748b', warn: false }
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

function canMoveTo(current: string, target: TaskStatus) {
  const normCurrent = current === 'OPEN' ? 'PENDING' : current
  if (normCurrent === 'PENDING') return target === 'IN_PROGRESS' || target === 'DONE'
  if (normCurrent === 'IN_PROGRESS') return target === 'DONE'
  return false
}



// ── Task Card ─────────────────────────────────────────────────────────────────

function TaskCard({
  task,
  onDragStart,
  onEdit,
  onDelete,
  isDragging,
  canDrag,
  canModify,
  canDelete,
}: {
  task: Task
  onDragStart: (t: Task) => void
  onEdit: (t: Task) => void
  onDelete: (t: Task) => void
  isDragging: boolean
  canDrag: boolean
  canModify: boolean
  canDelete: boolean
}) {
  const navigate = useNavigate()
  const isDone = task.status === 'DONE'
  const priority = PRIORITY_META[task.priority] ?? PRIORITY_META.LOW
  const due = formatDueDate(task.dueDate)

  return (
    <Box
      draggable={canDrag}
      onDragStart={() => canDrag && onDragStart(task)}
      sx={{
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        p: 1.75,
        mb: 1.5,
        cursor: canDrag ? 'grab' : 'default',
        opacity: isDragging ? 0.4 : 1,
        transition: 'box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          boxShadow: '0 4px 12px -2px rgba(0,0,0,0.08)',
          borderColor: '#cbd5e1',
          '&:active': { cursor: canDrag ? 'grabbing' : 'default' },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip
            label={priority.label}
            size="small"
            sx={{
              fontSize: '10.5px', fontWeight: 600, height: 18,
              bgcolor: priority.bg, color: priority.color, borderRadius: '4px',
              '& .MuiChip-label': { px: '6px' },
            }}
          />

          {task.leadName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Chip
                label="Lead"
                size="small"
                sx={{
                  fontSize: '10.5px', fontWeight: 500, height: 18,
                  bgcolor: `${themeColors.primary}12`, color: themeColors.primary, borderRadius: '4px',
                  '& .MuiChip-label': { px: '6px' },
                }}
              />
              {task.leadId && (
                <Tooltip title="View Lead Profile">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/leads/${task.leadId}`)}
                    sx={{ p: '2px', color: themeColors.primary }}
                  >
                    <RedirectIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {task.dealName && task.source === 'DEAL' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Chip
                label="Deal"
                size="small"
                sx={{
                  fontSize: '10.5px', fontWeight: 500, height: 18,
                  bgcolor: '#fefce8', color: '#ca8a04', borderRadius: '4px',
                  '& .MuiChip-label': { px: '6px' },
                }}
              />
              {task.dealId && (
                <Tooltip title="View Deal">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/deals/${task.dealId}?tab=tasks`)}
                    sx={{ p: '2px', color: '#ca8a04' }}
                  >
                    <RedirectIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {task.dealName && task.source === 'SALES' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Chip
                label="Sales"
                size="small"
                sx={{
                  fontSize: '10.5px', fontWeight: 500, height: 18,
                  bgcolor: '#ecfdf5', color: '#059669', borderRadius: '4px',
                  '& .MuiChip-label': { px: '6px' },
                }}
              />
              {task.dealId && (
                <Tooltip title="View Sales Record">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/sales/${task.dealId}`)}
                    sx={{ p: '2px', color: '#059669' }}
                  >
                    <RedirectIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {task.dealName && task.source === 'RETENTION' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Chip
                label="Retention"
                size="small"
                sx={{
                  fontSize: '10.5px', fontWeight: 500, height: 18,
                  bgcolor: '#fdf2f8', color: '#db2777', borderRadius: '4px',
                  '& .MuiChip-label': { px: '6px' },
                }}
              />
              {task.dealId && (
                <Tooltip title="View Retention Customer">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/customer-retention/${task.dealId}`)}
                    sx={{ p: '2px', color: '#db2777' }}
                  >
                    <RedirectIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {(canModify || canDelete) && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {canModify && !isDone && (
              <IconButton
                onClick={() => onEdit(task)}
                size="small"
                sx={{ color: '#475569', bgcolor: '#f8fafc', '&:hover': { bgcolor: '#eef2ff' } }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {canDelete && (
              <IconButton
                onClick={() => onDelete(task)}
                size="small"
                sx={{ color: '#dc2626', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <Typography sx={{
        fontSize: '13.5px', fontWeight: 600, color: '#1e293b',
        lineHeight: 1.4, mb: 1.25,
        textDecoration: isDone ? 'line-through' : 'none',
        opacity: isDone ? 0.6 : 1,
      }}>
        {task.title}
      </Typography>

      {task.description && (
        <Typography sx={{
          fontSize: '12px', color: '#475569', mb: 1.25, lineHeight: 1.5,
          whiteSpace: 'pre-line',
          textDecoration: isDone ? 'line-through' : 'none',
          opacity: isDone ? 0.7 : 0.9,
        }}>
          {task.description}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {due.warn
            ? <WarningIcon sx={{ fontSize: 12, color: due.color }} />
            : <ClockIcon sx={{ fontSize: 12, color: due.color }} />}
          <Typography sx={{ fontSize: '11px', fontWeight: 500, color: due.color }}>
            {due.text}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

// ── Kanban Column Component with Infinite Scroll ────────────────────────────

function InfiniteKanbanColumn({
  column,
  dragOverId,
  draggingTask,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  canDrag,
  canModify,
  canDelete,
  onEdit,
  onDelete,
  effectiveOrgIds,
  effectiveLobIds,
  effectiveOwnerIds,
  effectivePriorities,
  search,
  filters,
}: {
  column: Column
  dragOverId: TaskStatus | null
  draggingTask: Task | null
  onDragStart: (t: Task) => void
  onDragOver: (s: TaskStatus) => void
  onDragLeave: () => void
  onDrop: (s: TaskStatus) => void
  canDrag: boolean
  canModify: boolean
  canDelete: boolean
  onEdit: (t: Task) => void
  onDelete: (t: Task) => void
  effectiveOrgIds?: string[]
  effectiveLobIds?: string[]
  effectiveOwnerIds?: string[]
  effectivePriorities?: string[]
  search: string
  filters: UnifiedFilters
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const cleanSearch = search.trim() || undefined

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['tasks', 'infinite', column.id, effectiveOrgIds, effectiveLobIds, effectiveOwnerIds, effectivePriorities, cleanSearch],
    queryFn: ({ pageParam = 1 }) =>
      tasksApi.getMyTasks({
        status: column.id,
        search: cleanSearch,
        page: pageParam,
        pageSize: 15,
        orgIds: effectiveOrgIds,
        lobIds: effectiveLobIds,
        ownerIds: effectiveOwnerIds,
        priorities: effectivePriorities,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 30_000,
  })

  // Infinite Scroll Observer
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage || isFetchingNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const allItems = useMemo(() => {
    return data?.pages.flatMap(p => p.items) ?? []
  }, [data])

  const filteredTasks = useMemo(() => {
    return allItems.filter(task => {
      if (cleanSearch) {
        const q = cleanSearch.toLowerCase()
        if (!task.title?.toLowerCase().includes(q) &&
          !task.leadName?.toLowerCase().includes(q) &&
          !task.dealName?.toLowerCase().includes(q) &&
          !task.assignedTo?.name?.toLowerCase().includes(q)) {
          return false
        }
      }
      const selectedPriorities = filters.checkboxes?.priority || []
      if (selectedPriorities.length > 0) {
        const upperSelected = selectedPriorities.map(p => p.toUpperCase())
        if (!upperSelected.includes((task.priority || '').toUpperCase())) {
          return false
        }
      }
      if (filters.datePreset) {
        const d = task.dueDate || task.createdAt
        if (!applyUnifiedDateFilter(d, filters)) {
          return false
        }
      }
      return true
    })
  }, [allItems, cleanSearch, filters])

  const totalCount = data?.pages[0]?.total ?? filteredTasks.length

  const isOver = dragOverId === column.id
  const canDrop = draggingTask && canMoveTo(draggingTask.status, column.id)

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver(column.id)
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault()
        onDrop(column.id)
      }}
      sx={{
        flex: 1,
        minWidth: 280,
        bgcolor: isOver ? column.bgColor : '#f8fafc',
        borderRadius: '12px',
        border: isOver ? `2px dashed ${column.color}` : '1px solid #e2e8f0',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 165px)',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      }}
    >
      {/* Column Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: column.dotColor }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>
            {column.label}
          </Typography>
        </Box>
        <Chip
          label={totalCount}
          size="small"
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            height: 20,
            bgcolor: column.bgColor,
            color: column.color,
            '& .MuiChip-label': { px: '8px' },
          }}
        />
      </Box>

      {/* Cards list */}
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: column.color }} />
          </Box>
        ) : filteredTasks.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 120, opacity: 0.35 }}>
            <TaskIcon sx={{ fontSize: 28, color: '#94a3b8', mb: 0.5 }} />
            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>No tasks</Typography>
          </Box>
        ) : (
          <>
            {filteredTasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onDragStart={onDragStart}
                onEdit={onEdit}
                onDelete={onDelete}
                isDragging={draggingTask?.id === task.id}
                canDrag={canDrag && task.status !== 'DONE'}
                canModify={canModify}
                canDelete={canDelete}
              />
            ))}

            {/* Infinite Scroll Sentinel */}
            <Box ref={sentinelRef} sx={{ textAlign: 'center', py: 1 }}>
              {isFetchingNextPage && <CircularProgress size={20} sx={{ color: column.color }} />}
            </Box>
          </>
        )}
      </Box>

      {/* Drop hint */}
      {canDrop && (
        <Box sx={{ textAlign: 'center', py: 1.5, borderRadius: '8px', bgcolor: 'rgba(92,53,216,0.06)', mt: 1 }}>
          <Typography sx={{ fontSize: '12px', color: themeColors.primary, fontWeight: 600 }}>
            Move to {column.label}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function TasksPage() {
  const qc = useQueryClient()

  // ── Permissions ──
  const { hasAction } = usePermission()
  const canUpdate = hasAction('/tasks', 'update')
  const canDelete = hasAction('/tasks', 'delete')
  const canDragDrop = hasAction('/tasks', 'update_pipeline')

  const { isSuperAdmin } = useOrgSelector()
  const navOrgFilter = useOrgFilter()

  const userOrgId = authStore.user?.orgId ?? ''
  const userLobId = authStore.user?.lobId ?? ''
  const isOrgAdmin = !isSuperAdmin && !!userOrgId && !userLobId

  const [draggingTask, setDraggingTask] = useState<Task | null>(null)
  const [dragOverColumn, setDragOverColumn] = useState<TaskStatus | null>(null)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [deletingTask, setDeletingTask] = useState<Task | null>(null)
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-tasks-filters')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return { ...EMPTY_UNIFIED_FILTERS, ...parsed }
      } catch { }
    }
    return EMPTY_UNIFIED_FILTERS
  })

  useEffect(() => {
    sessionStorage.setItem('crm-tasks-filters', JSON.stringify(filters))
  }, [filters])

  const [filterOpen, setFilterOpen] = useState(false)
  const activeCount = countActiveFilters(filters)

  const { data: orgs = [] } = useQuery({ queryKey: ['admin-orgs'], queryFn: orgsApi.getAll, enabled: isSuperAdmin, staleTime: 60_000 })
  const orgIdForFetching = filters.checkboxes.orgId?.[0] || navOrgFilter.orgId
  const lobOrgIdForFetching = isSuperAdmin ? orgIdForFetching : userOrgId
  const { data: lobsForFilter = [] } = useQuery({
    queryKey: ['admin-lobs', lobOrgIdForFetching],
    queryFn: () => lobsApi.getAll(lobOrgIdForFetching!),
    enabled: (isSuperAdmin || isOrgAdmin) && !!lobOrgIdForFetching,
    staleTime: 30_000,
  })

  const effectiveOrgIds = isSuperAdmin && filters.checkboxes.orgId?.length ? filters.checkboxes.orgId : undefined
  const effectiveLobIds = (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length ? filters.checkboxes.lobId : undefined
  const effectiveOwnerIds = filters.ownerIds?.length ? filters.ownerIds : undefined
  const effectivePriorities = filters.checkboxes?.priority?.length ? filters.checkboxes.priority : undefined

  const TASK_FILTERS: FilterDef[] = useMemo(() => {
    const defs = [...BASE_TASK_FILTERS]
    if (authStore.canViewLobWideData) {
      defs.push({ type: 'owner', label: 'Owner' })
    }
    if (isSuperAdmin) {
      defs.push(
        { type: 'checkbox', key: 'orgId', label: 'Organisation', options: orgs.map(o => ({ label: o.name, value: o.id })) },
        { type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) },
      )
    } else if (isOrgAdmin) {
      defs.push({ type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) })
    }
    return defs
  }, [isSuperAdmin, isOrgAdmin, orgs, lobsForFilter])

  const cleanSearch = search.trim() || undefined

  // Summary Query
  const { data: summary } = useQuery({
    queryKey: ['tasks', 'summary', effectiveOrgIds, effectiveLobIds, effectiveOwnerIds, effectivePriorities, cleanSearch],
    queryFn: () => tasksApi.getMySummary({
      search: cleanSearch,
      orgIds: effectiveOrgIds,
      lobIds: effectiveLobIds,
      ownerIds: effectiveOwnerIds,
      priorities: effectivePriorities,
    }),
    staleTime: 30_000,
  })

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, task }: { id: string; status: TaskStatus; task?: Task }) => {
      if (task?.source === 'SALES' && task.dealId) {
        return salesApi.updateTask(task.dealId, task.id, { status })
      }
      if (task?.source === 'RETENTION' && task.dealId) {
        return retentionApi.updateTask(task.dealId, task.id, { status })
      }
      return tasksApi.updateStatus(id, status)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'infinite'] })
      toast.success('Task status updated')
    },
    onError: (e: any) => {
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'infinite'] })
      toast.error(e?.response?.data?.message ?? 'Failed to update task status')
    },
  })

  const handleDragStart = (task: Task) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
    setDraggingTask(task)
  }

  const handleDragOver = (status: TaskStatus) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
    setDragOverColumn(status)
  }

  const handleDragLeave = () => {
    leaveTimer.current = setTimeout(() => setDragOverColumn(null), 80)
  }

  const handleOpenEdit = (task: Task) => {
    setEditingTask(task)
  }

  const handleCloseEdit = () => {
    setEditingTask(null)
  }

  const handleDeleteTask = (task: Task) => {
    setDeletingTask(task)
  }

  const deleteMut = useMutation({
    mutationFn: async (task: Task) => {
      const userId = authStore.user?.userId

      if (task.source === 'SALES' && task.dealId) {
        await salesApi.deleteTask(task.dealId, task.id)
        return
      }
      if (task.source === 'RETENTION' && task.dealId) {
        await retentionApi.deleteTask(task.dealId, task.id)
        return
      }
      if (task.dealId) {
        await dealsApi.deleteTask(task.dealId, task.id)
        return
      }
      if (task.leadId) {
        await leadTasksApi.update(task.leadId, task.id, { status: 'CANCELLED' })
        if (userId) {
          await leadsApi.addActivity(task.leadId, {
            type: 'TASK',
            description: `Task deleted: ${task.title}`,
            performedById: userId,
            metadataJson: null,
          })
        }
        return
      }
      await tasksApi.delete(task.id)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'infinite'] })
      toast.success('Task deleted')
      setDeletingTask(null)
    },
    onError: (e: any) => {
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'infinite'] })
      toast.error(e?.response?.data?.message ?? 'Failed to delete task')
    }
  })

  const updateTaskMutation = useMutation({
    mutationFn: async ({ task, payload }: { task: Task; payload: { title: string; description?: string; priority: TaskPriority; dueDate: string; status?: string } }) => {
      if (task.source === 'SALES' && task.dealId) {
        await salesApi.updateTask(task.dealId, task.id, {
          title: payload.title,
          description: payload.description || null,
          priority: payload.priority,
          dueDate: payload.dueDate || null,
          status: payload.status,
        })
        return
      }
      if (task.source === 'RETENTION' && task.dealId) {
        await retentionApi.updateTask(task.dealId, task.id, {
          title: payload.title,
          description: payload.description || null,
          priority: payload.priority,
          dueDate: payload.dueDate || null,
          status: payload.status,
        })
        return
      }
      if (task.dealId) {
        await dealsApi.updateTask(task.dealId, task.id, {
          title: payload.title,
          description: payload.description || null,
          priority: payload.priority,
          dueDate: payload.dueDate || null,
          status: payload.status,
        })
        return
      }
      if (task.leadId) {
        await leadTasksApi.update(task.leadId, task.id, {
          title: payload.title,
          description: payload.description || null,
          priority: payload.priority,
          dueDate: payload.dueDate || null,
          status: payload.status as any,
        })
        return
      }
      throw new Error('Task update is not supported for this item')
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'infinite'] })
      toast.success('Task updated')
      setEditingTask(null)
    },
    onError: (e: any) => {
      qc.invalidateQueries({ queryKey: ['tasks', 'summary'] })
      qc.invalidateQueries({ queryKey: ['tasks', 'infinite'] })
      toast.error(e?.response?.data?.message ?? 'Failed to update task')
    },
  })

  const handleDrop = (targetStatus: TaskStatus) => {
    setDragOverColumn(null)
    if (draggingTask && canMoveTo(draggingTask.status, targetStatus)) {
      const newStatus = (targetStatus === 'PENDING' && (draggingTask.dealId || draggingTask.source !== 'LEAD')) ? 'OPEN' : targetStatus
      updateStatusMutation.mutate({ id: draggingTask.id, status: newStatus as TaskStatus, task: draggingTask })
    }
    setDraggingTask(null)
  }

  const handleDragEnd = () => {
    setDraggingTask(null)
    setDragOverColumn(null)
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onDragEnd={handleDragEnd}
    >
      <PageHeader
        title="Tasks"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SearchBar
              value={search}
              onSearch={setSearch}
              placeholder="Search tasks..."
              sx={{ height: '38px', width: { xs: '100%', sm: 260 } }}
            />

            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
              onClick={() => setFilterOpen(true)}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '13px',
                height: '38px',
                py: '8px',
                px: 1.75,
                color: activeCount > 0 ? themeColors.primary : '#64748b',
                borderColor: activeCount > 0 ? themeColors.primary : '#e2e8f0',
                bgcolor: activeCount > 0 ? `${themeColors.primary}08` : '#fff',
                '& .MuiButton-startIcon': { ml: 0, mr: 0.75 },
                '& .MuiButton-endIcon': { ml: 0.75, mr: 0 },
                '&:hover': {
                  borderColor: themeColors.primary,
                  bgcolor: `${themeColors.primary}08`,
                },
              }}
            >
              Filters
              {activeCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    minWidth: 18,
                    height: 18,
                    borderRadius: '9px',
                    bgcolor: themeColors.primary,
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: '4px',
                  }}
                >
                  {activeCount}
                </Box>
              )}
            </Button>

            {activeCount > 0 && (
              <Tooltip title="Reset Filters">
                <IconButton
                  size="small"
                  onClick={() => setFilters(EMPTY_UNIFIED_FILTERS)}
                  sx={{
                    height: '38px',
                    width: '38px',
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0',
                    // bgcolor: '#fff',
                    color: '#64748b',
                    '&:hover': {
                      color: themeColors.primary,
                      // borderColor: themeColors.primary,
                      bgcolor: `${themeColors.primary}08`,
                    },
                  }}
                >
                  <FilterAltOffIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
      />



      {/* Kanban Board with Infinite Scroll */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', height: '100%' }}>
          {COLUMNS.map(col => (
            <InfiniteKanbanColumn
              key={col.id}
              column={col}
              dragOverId={dragOverColumn}
              draggingTask={draggingTask}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              canDrag={canDragDrop}
              canModify={canUpdate}
              canDelete={canDelete}
              onEdit={handleOpenEdit}
              onDelete={handleDeleteTask}
              effectiveOrgIds={effectiveOrgIds}
              effectiveLobIds={effectiveLobIds}
              effectiveOwnerIds={effectiveOwnerIds}
              effectivePriorities={effectivePriorities}
              search={search}
              filters={filters}
            />
          ))}
        </Box>
      </Box>

      <UnifiedFilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={(f) => setFilters(f)}
        filterDefs={TASK_FILTERS}
        initialFilters={filters}
      />

      <TaskModal
        open={Boolean(editingTask)}
        task={editingTask}
        entityType={editingTask?.dealId ? 'deal' : 'lead'}
        onClose={handleCloseEdit}
        onSave={(p) => editingTask && updateTaskMutation.mutate({ task: editingTask, payload: p as any })}
        isSaving={updateTaskMutation.isPending}
      />

      <DeleteDialog
        open={Boolean(deletingTask)}
        title="Delete Task"
        name={deletingTask?.title}
        onClose={() => setDeletingTask(null)}
        onConfirm={() => deletingTask && deleteMut.mutate(deletingTask)}
        isDeleting={deleteMut.isPending}
      />
    </Box>
  )
}
